// Capacity Sandbox — pure feasibility math (Mode 1).
// Required work per resource group = Σ_parts (weeklyQty × cycleTime_in_group). Available =
// stations × seconds/week (24/7). Utilization = required / available. Setups are omitted here —
// at foundry volumes they're <0.2% of load and would only add noise to a design-altitude tool.

const SEC_PER_WEEK = 7 * 24 * 3600; // 604800, matches the 24/7 ResourceCalendar the planner uses

// severity ladder (tech: worst-group utilization → a named state + a --cn-* status color)
export function severityOf(util) {
  if (!isFinite(util)) return { key: 'severe', label: 'No capacity', colorVar: '--cn-danger' };
  if (util < 0.85)     return { key: 'healthy', label: 'Healthy', colorVar: '--cn-success' };
  if (util <= 1.0)     return { key: 'near', label: 'Near constraint', colorVar: '--cn-warning' };
  if (util <= 2.0)     return { key: 'infeasible', label: 'Infeasible', colorVar: '--cn-danger' };
  return { key: 'severe', label: 'Severely overloaded', colorVar: '--cn-danger' };
}

export function statusColorVar(util) {
  return util > 1 ? '--cn-danger' : util > 0.85 ? '--cn-warning' : '--cn-success';
}

// parts:     [{ id, operations: [{ machineType, cycleTime }] }]  (only the scenario's parts)
// demand:    { [partId]: weeklyQty }
// stations:  { [group]: count }   (hypothetical plant)
// cycleMult: { [group]: number }  (hypothetical process speed; 1 = as-routed, 0.8 = 20% faster)
// Returns per-group rows + overall feasibility, bottleneck, and sizing answers.
export function sandboxCapacity({ parts, demand, stations, cycleMult = {}, secPerWeek = SEC_PER_WEEK }) {
  const partById = Object.fromEntries(parts.map((p) => [p.id, p]));
  const mult = (g) => cycleMult[g] ?? 1;

  // group set = the machine types the scenario's parts actually route through
  const required = {};
  for (const p of parts) for (const op of (p.operations || [])) required[op.machineType] ??= 0;
  for (const [pid, qty] of Object.entries(demand)) {
    const p = partById[pid];
    if (!p) continue;
    for (const op of (p.operations || [])) required[op.machineType] += (qty || 0) * (op.cycleTime || 0) * mult(op.machineType);
  }

  const rows = Object.entries(required).map(([key, reqSec]) => {
    const count = stations[key] ?? 0;
    const availSec = count * secPerWeek;
    const util = availSec > 0 ? reqSec / availSec : Infinity;
    const requiredStations = Math.max(1, Math.ceil(reqSec / secPerWeek));
    return { key, count, reqSec, availSec, util, requiredStations, delta: requiredStations - count };
  });

  const worst = rows.reduce((a, b) => (b.util > (a?.util ?? -Infinity) ? b : a), null);
  const total = Object.values(demand).reduce((s, q) => s + (q || 0), 0);
  const worstUtil = worst?.util ?? 0;
  const maxFeasible = isFinite(worstUtil) && worstUtil > 0 ? total / worstUtil : total;

  return {
    rows, worst, total,
    maxFeasible, gap: maxFeasible - total,
    feasible: worstUtil <= 1,
    severity: severityOf(worstUtil),
  };
}

export { SEC_PER_WEEK };
